
import React, { useEffect, useState } from 'react';
import { Contestant, AIAnalysis } from '../types';
import { getContestantAnalysis } from '../services/geminiService';

interface VideoModalProps {
  contestant: Contestant | null;
  onClose: () => void;
}

function getYouTubeID(url: string) {
  const regExp =
    /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/i;
  const match = url.match(regExp);
  return match ? match[1] : "";
}

const VideoModal: React.FC<VideoModalProps> = ({ contestant, onClose }) => {
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (contestant) {
      setLoading(true);
      getContestantAnalysis(contestant.name, contestant.bio)
        .then(res => setAnalysis(res))
        .catch(() => setAnalysis(null))
        .finally(() => setLoading(false));
    } else {
      setAnalysis(null);
    }
  }, [contestant]);

  if (!contestant) return null;

  const videoId = getYouTubeID(contestant.videoUrl);
  // Using reliable embed format with clean parameters
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${window.location.origin}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-md animate-in fade-in duration-300">
      <div className="relative w-full max-w-5xl bg-slate-900 rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row border border-pink-500/30">
        
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-black/50 text-white hover:bg-pink-600 transition-all duration-300 transform hover:scale-110"
          aria-label="Close Modal"
        >
          <i className="fas fa-times text-lg"></i>
        </button>

        {/* Video Side */}
        <div className="w-full md:w-2/3 bg-black flex items-center justify-center shadow-inner">
          <div className="relative w-full pb-[56.25%]">
            <iframe
              src={embedUrl}
              title={`${contestant.name} Introduction`}
              className="absolute top-0 left-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>
          </div>
        </div>

        {/* Content Side */}
        <div className="w-full md:w-1/3 p-6 md:p-8 flex flex-col overflow-y-auto max-h-[50vh] md:max-h-[80vh] border-t md:border-t-0 md:border-l border-slate-800 bg-slate-900/50">
          <span className="text-pink-500 font-semibold tracking-widest text-[10px] uppercase mb-2">Season {contestant.season} Premiere</span>
          <h2 className="text-3xl font-playfair font-bold text-white mb-1 leading-tight">{contestant.name}</h2>
          <p className="text-pink-400 italic text-sm mb-6">"{contestant.tagline}"</p>
          
          <div className="mb-6">
            <h3 className="text-[10px] font-bold text-slate-500 uppercase mb-2 tracking-widest">Contestant Profile</h3>
            <p className="text-slate-300 text-sm leading-relaxed">{contestant.bio}</p>
          </div>

          <div className="mt-auto border-t border-slate-800 pt-6">
            <h3 className="text-xs font-bold text-pink-500 flex items-center gap-2 mb-4 uppercase tracking-widest">
              <i className="fas fa-sparkles text-[10px]"></i> AI Personality Insight
            </h3>
            
            {loading ? (
              <div className="space-y-3 animate-pulse">
                <div className="h-4 bg-slate-800 rounded w-full opacity-50"></div>
                <div className="h-4 bg-slate-800 rounded w-3/4 opacity-30"></div>
                <div className="h-24 bg-slate-800 rounded w-full opacity-20"></div>
              </div>
            ) : analysis && (
              <div className="space-y-4 animate-in slide-in-from-bottom-2 duration-500">
                <div className="flex flex-wrap gap-2">
                  {analysis.personalityTraits.map((trait, i) => (
                    <span key={i} className="px-2 py-1 bg-pink-500/10 border border-pink-500/20 rounded text-[9px] text-pink-300 font-bold uppercase tracking-tight">
                      {trait}
                    </span>
                  ))}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                   <div className="p-3 bg-slate-800/40 rounded-xl border border-white/5 hover:border-pink-500/20 transition-colors">
                    <p className="text-[8px] text-slate-500 uppercase font-black mb-1 tracking-tighter">Drama Quotient</p>
                    <div className="flex items-center gap-1">
                      <span className="text-xl font-bold text-pink-400">{analysis.dramaScore}</span>
                      <span className="text-[10px] text-slate-600">/10</span>
                    </div>
                  </div>
                  <div className="p-3 bg-slate-800/40 rounded-xl border border-white/5 hover:border-pink-500/20 transition-colors">
                    <p className="text-[8px] text-slate-500 uppercase font-black mb-1 tracking-tighter">Villa Role</p>
                    <p className="text-xs font-bold text-blue-400 truncate">{analysis.vibes}</p>
                  </div>
                </div>

                <div className="p-3 bg-pink-500/5 border border-pink-500/10 rounded-xl relative">
                   <div className="absolute -top-1.5 -left-1.5 text-pink-500/30"><i className="fas fa-quote-left text-xs"></i></div>
                   <p className="text-[13px] text-slate-300 leading-snug italic">
                    {analysis.summary}
                   </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoModal;
